import { ApolloClient, ApolloLink, DefaultOptions, HttpLink, InMemoryCache, split } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { WebSocketLink } from '@apollo/client/link/ws'
import { getMainDefinition } from '@apollo/client/utilities'
import ws from 'ws'
import { appContext } from '../../common/src/context'
import { getWindow } from '../../common/src/global'

const window = getWindow()

export const NETWORK_ONLY_FETCH_POLICY = 'network-only'

const wsLinkUri = appContext()?.isProduction
  ? `wss://pubsub.${location.host}`
  : 'ws://localhost:8888/apigatewaywebsocket'

const wsLink = new WebSocketLink({
  uri: wsLinkUri,
  options: {
    reconnect: true,
  },
  webSocketImpl: window ? undefined : ws,
})

const httpLink = new HttpLink({
  uri: '/graphql',
  credentials: 'same-origin',
  fetch: (input: RequestInfo | URL, init?: RequestInit) => {
    const reqBody = JSON.parse(init!.body! as string)
    const opName = reqBody.operationName
    const actionName = reqBody.variables?.action?.actionName
    const url = input instanceof Request ? input.url : input.toString()
    const actionNameParam = actionName ? '&actionName=' + actionName : ''
    return fetch(`${url}?opName=${opName}${actionNameParam}`, init)
  },
})

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) =>
      console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
    )
  }

  if (networkError) {
    console.error(`[Network error]: ${networkError}`)
  }
})

const authLink = setContext((_, { headers }) => {
  const h = Object.assign({}, headers, { 'X-CSRF': 'csrf' })
  return {
    headers: h,
  }
})

// Redirect Subscription request to wsLink, any other request to httpLink.
const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  },
  wsLink,
  httpLink
)

export const apolloLinkForCC = authLink.concat(errorLink).concat(splitLink)

const createApolloClient = (link: ApolloLink, defaultOptions?: DefaultOptions) => {
  const cache = new InMemoryCache({
    possibleTypes: {
      // See https://www.apollographql.com/docs/react/caching/cache-configuration/.
      Link: ['LeasingTaskLink', 'FloorPlanLink'],
    },
    // TODO: clean this
    // See https://www.apollographql.com/docs/react/caching/cache-field-behavior/#merging-non-normalized-objects
    typePolicies: {
      Query: {
        fields: {
          notes: {
            merge(existing, incoming) {
              return incoming
            },
          },
        },
      },
      LeasingTask: {
        fields: {
          leasingInfo: {
            merge(existing, incoming) {
              return incoming
            },
          },
          archivedThreads: {
            merge(existing, incoming) {
              return incoming
            },
          },
          taskAgents: {
            merge(existing, incoming) {
              return incoming
            },
          },
        },
      },
      Thread: {
        fields: {
          botThread: {
            merge(existing, incoming) {
              return incoming
            },
          },
        },
      },
    },
  })

  cache.evict({
    // Often cache.evict will take an options.id property, but that's not necessary
    // when evicting from the ROOT_QUERY object, as we're doing here.
    fieldName: 'notifications',
    // No need to trigger a broadcast here, since writeQuery will take care of that.
    broadcast: false,
  })

  const apolloClient = new ApolloClient({ link, cache, defaultOptions })

  // For debugging:
  const ctx = appContext()
  ctx.apolloLink = link
  ctx.apolloCache = cache
  ctx.apolloClient = apolloClient

  return apolloClient
}

/**
 * This is the Apollo (graphql) client for Command Center.
 * It lets a logged-in operator or admin make queries, mutations, and subscriptions.
 */
export const apolloWebClient = createApolloClient(apolloLinkForCC)
